import { useMemo, useState, type MouseEvent } from 'react'
import type { TagCategory, TrainingTagDto } from '../../api/trainingBlockApi'
import { useTranslation } from '../../i18n/useTranslation'
import { useTheme } from '../../hooks/useTheme'
import {
  emptyTagCategoryFilterState,
  isTagCategoryFilterActive,
  type TagCategoryFilterState,
} from './tagCategoryFilterUtils'

interface TagCategoryFilterProps {
  tags: TrainingTagDto[]
  usedTagKeys: Set<string>
  categories: TagCategory[]
  value: TagCategoryFilterState
  onChange: (next: TagCategoryFilterState) => void
  className?: string
  /** Bleed chips to page edges (use on screens with p-5). */
  bleed?: boolean
  /** Whether the filter panel starts expanded. Defaults to collapsed. */
  defaultExpanded?: boolean
}

export function TagCategoryFilter({
  tags,
  usedTagKeys,
  categories,
  value,
  onChange,
  className = '',
  bleed = true,
  defaultExpanded = false,
}: TagCategoryFilterProps) {
  const theme = useTheme()
  const { t } = useTranslation()
  const [expanded, setExpanded] = useState(defaultExpanded)

  const rows = useMemo(() => {
    return categories
      .map(category => ({
        category,
        tags: tags.filter(tag => tag.category === category && usedTagKeys.has(tag.key)),
      }))
      .filter(row => row.tags.length > 0)
  }, [categories, tags, usedTagKeys])

  const selectedTags = useMemo(() => {
    const selectedKeys = new Set<string>()
    for (const set of Object.values(value)) {
      if (set) for (const key of set) selectedKeys.add(key)
    }
    return tags.filter(tag => selectedKeys.has(tag.key))
  }, [tags, value])

  const selectedCount = selectedTags.length

  const selectedSummary = useMemo(() => {
    return selectedTags
      .map(tag => {
        const label = t.trainingTags[tag.key as keyof typeof t.trainingTags] ?? tag.label
        return `${tag.emoji} ${label}`
      })
      .join(' · ')
  }, [selectedTags, t])

  void bleed // kept for API compatibility; chips wrap inside the card

  if (rows.length === 0) return null

  const active = isTagCategoryFilterActive(value)

  function toggle(category: TagCategory, key: string) {
    const current = new Set(value[category] ?? [])
    if (current.has(key)) current.delete(key)
    else current.add(key)
    const next: TagCategoryFilterState = { ...value }
    if (current.size === 0) delete next[category]
    else next[category] = current
    onChange(next)
  }

  function clearAll(e: MouseEvent) {
    e.stopPropagation()
    onChange(emptyTagCategoryFilterState())
  }

  return (
    <div
      className={`rounded-2xl overflow-hidden ${className}`}
      style={{
        backgroundColor: theme.cardBg,
        border: `1px solid ${active ? `${theme.buttonColor}60` : theme.cardBorder}`,
      }}
    >
      {/* Collapsed header — tap to expand/collapse */}
      <button
        type="button"
        className="w-full flex items-center gap-3 px-4 min-h-[48px] py-2 text-left"
        onClick={() => setExpanded(v => !v)}
        aria-expanded={expanded}
      >
        <span className="text-lg leading-none shrink-0" aria-hidden>
          🏷️
        </span>

        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <p className="text-sm font-semibold" style={{ color: theme.text.primary }}>
              {t.trainingBlocks.filterTitle}
            </p>
            {selectedCount > 0 && (
              <span
                className="inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full text-[11px] font-bold"
                style={{
                  backgroundColor: `${theme.buttonColor}25`,
                  color: theme.accent.text,
                }}
              >
                {selectedCount}
              </span>
            )}
          </div>
          <p className="text-xs truncate mt-0.5" style={{ color: theme.text.muted }}>
            {active ? selectedSummary : t.trainingBlocks.filterCollapsedHint}
          </p>
        </div>

        {active && (
          <button
            type="button"
            onClick={clearAll}
            className="text-xs font-semibold shrink-0 min-h-[44px] px-2"
            style={{ color: theme.accent.text }}
          >
            {t.trainingBlocks.filterClear}
          </button>
        )}

        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="16"
          height="16"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          className="shrink-0 transition-transform duration-200"
          style={{
            color: theme.text.muted,
            transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
          }}
          aria-hidden
        >
          <polyline points="6 9 12 15 18 9" />
        </svg>
      </button>

      {/* Expanded category chips */}
      {expanded && (
        <div
          className="flex flex-col gap-3 px-4 pb-3.5 pt-1"
          style={{ borderTop: `1px solid ${theme.cardBorder}` }}
        >
          {rows.map(({ category, tags: rowTags }) => {
            const selected = value[category] ?? new Set<string>()
            return (
              <div key={category}>
                <p
                  className="text-[11px] font-medium mb-1.5 opacity-70"
                  style={{ color: theme.text.muted }}
                >
                  {t.trainingTagSections[category]}
                </p>
                <div className="flex flex-wrap gap-2">
                  {rowTags.map(tag => {
                    const isOn = selected.has(tag.key)
                    const label = t.trainingTags[tag.key as keyof typeof t.trainingTags] ?? tag.label
                    return (
                      <button
                        key={tag.key}
                        type="button"
                        onClick={() => toggle(category, tag.key)}
                        className="flex items-center gap-1.5 min-h-[40px] px-3 rounded-full text-sm border transition-all whitespace-nowrap active:scale-95"
                        style={isOn ? {
                          backgroundColor: `${theme.buttonColor}20`,
                          borderColor: `${theme.buttonColor}60`,
                          color: theme.accent.text,
                          fontWeight: 600,
                        } : {
                          backgroundColor: theme.isDark ? 'rgba(255,255,255,0.06)' : '#ffffff',
                          borderColor: theme.cardBorder,
                          color: theme.text.muted,
                        }}
                      >
                        <span className="text-base leading-none">{tag.emoji}</span>
                        <span>{label}</span>
                      </button>
                    )
                  })}
                </div>
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}
